import React, { useState, useEffect, useRef } from 'react';
import { X, FolderInput, Search, Briefcase } from 'lucide-react';
import { documentService, caseService } from '../../services/api';
import useModal from '../../hooks/useModal';

const MoveDocumentModal = ({ document, isOpen, onClose, onMoved }) => {
  const [search, setSearch] = useState('');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [error, setError] = useState('');
  const searchRef = useRef(null);

  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);

  // Focus search input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 100);
      setSearch('');
      setCases([]);
      setSelectedCase(null);
      setError('');
    }
  }, [isOpen]);

  // Search cases with debounce
  useEffect(() => {
    if (!search.trim()) {
      setCases([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await caseService.getCases({ search: search.trim(), limit: 10 });
        const results = Array.isArray(res.data) ? res.data : res.data?.data || [];
        // Filter out the current case
        setCases(results.filter(c => c.id !== document?.case_id));
      } catch {
        setCases([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search, document?.case_id]);

  const handleSubmit = async () => {
    if (!selectedCase || !document) return;
    setIsSubmitting(true);
    setError('');

    try {
      await documentService.updateDocument(document.id, {
        case_id: selectedCase.id,
        folder_id: null, // Clear folder since folders are case-specific
      });
      if (onMoved) onMoved(selectedCase);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to move document');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  if (!isOpen || !document) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FolderInput className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Move Document</h3>
              <p className="text-sm text-gray-500">Transfer to a different case</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Document Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-900 truncate">{document.original_file_name}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Currently on case {document.case_number || document.case_id}
            </p>
          </div>

          {/* Case Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Destination case
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSelectedCase(null); }}
                placeholder="Search by case number or title..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                disabled={isSubmitting}
              />
            </div>

            {/* Results */}
            {search.trim() && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                {loading ? (
                  <div className="p-3 text-center text-sm text-gray-500">Searching...</div>
                ) : cases.length === 0 ? (
                  <div className="p-3 text-center text-sm text-gray-500">No cases found</div>
                ) : (
                  cases.map(c => (
                    <button
                      key={c.id}
                      onClick={() => { setSelectedCase(c); setSearch(`${c.case_number} - ${c.title}`); }}
                      className={`w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center space-x-2 border-b border-gray-100 last:border-b-0 transition-colors ${
                        selectedCase?.id === c.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <Briefcase className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{c.case_number} - {c.title}</p>
                        {c.client_name && (
                          <p className="text-xs text-gray-500 truncate">{c.client_name}</p>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedCase || isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Moving...
              </>
            ) : (
              <>
                <FolderInput className="h-4 w-4 mr-2" />
                Move Document
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveDocumentModal;
